import React, { useEffect, useState } from 'react';

import { useTheme } from '../../../theme/ThemeContext';
import useSittingStore from '../../../stores/sittingStore';
import useSittingActions from '../../../hooks/useSittingActions';
import ProcedureChipList from './ProcedureChipList';
import QuickSearchAdd from './QuickSearchAdd';
import SavedCombosPicker from './SavedCombosPicker';
import SaveAsCombo from './SaveAsCombo';
import TotalsRibbon from './TotalsRibbon';

/**
 * ExpandedProcedureCanvas — full-screen editor that opens when the
 * doctor clicks ⤢ on the half-disc widget.
 *
 * Phase 3.0a (this build): a wider, dual-column layout that reuses the
 * same building blocks as the disc — search palette + chip list +
 * combos + totals — with more breathing room. Drag-and-drop (`dnd-kit`)
 * lands in Phase 3.0b. Until then this view is functionally equivalent
 * to the disc but readable on a 1080p screen and easier on the eyes.
 *
 * Layout:
 *   ┌─ header ──────────────────────────────────────────────┐
 *   │ Procedures · Anita                            ⤡ Close │
 *   ├─ left column ─────────────┬─ right column ────────────┤
 *   │ 🔎 Search & add procedure │ Today's procedures        │
 *   │ 📘 Saved combos           │ ── chip list ──           │
 *   │                           │ ──────────────            │
 *   │                           │ Totals · Save as combo    │
 *   └───────────────────────────────────────────────────────┘
 */
export default function ExpandedProcedureCanvas({
  visit,
  viewer,
  onClose,
}) {
  const { isDark } = useTheme();
  const visitId = visit?.visit_id;

  const byVisit = useSittingStore((s) => s.byVisit);
  const fetchForVisit = useSittingStore((s) => s.fetchForVisit);
  const sittings = (visitId && byVisit?.[visitId]) || [];

  const [view, setView] = useState('search'); // 'search' | 'combos'

  useEffect(() => {
    if (visitId && fetchForVisit) fetchForVisit(visitId);
  }, [visitId, fetchForVisit]);

  // Lock background scroll while the canvas is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const reload = () => {
    if (visitId && fetchForVisit) fetchForVisit(visitId);
  };

  const { handleAction: rawHandleAction, dialogs } = useSittingActions({
    viewer,
    onAfterAction: reload,
  });

  // Canvas mirrors the half-disc — it's the same single-visit surface
  // expanded full-screen, so the patient is already disambiguated.
  // Skip the start-confirmation dialog to match disc behaviour.
  const handleAction = (event) => rawHandleAction({ ...event, skipConfirm: true });

  const handleDelete = (sitting) => handleAction({ action: 'cancel', sitting });

  const palette = {
    bg:      isDark ? '#0f172a' : '#ffffff',
    bgAlt:   isDark ? '#1e293b' : '#f8fafc',
    border:  isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.12)',
    text:    isDark ? '#e2e8f0' : '#1e293b',
    muted:   isDark ? '#94a3b8' : '#64748b',
  };

  const patientLabel =
    visit?.patient_name
    || visit?.patient_full_name
    || visit?.patient_id
    || 'Patient';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Procedures editor"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        background: isDark ? 'rgba(2,6,23,0.85)' : 'rgba(15,23,42,0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        padding: '1.5rem',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        style={{
          width: 'min(1080px, 100%)',
          height: '100%',
          background: palette.bg,
          color: palette.text,
          border: `1px solid ${palette.border}`,
          borderRadius: 20,
          boxShadow: '0 40px 80px -24px rgba(0,0,0,0.5), 0 12px 28px -16px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '0.875rem 1.25rem',
            borderBottom: `1px solid ${palette.border}`,
            background: palette.bgAlt,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 38, height: 38, borderRadius: 12,
              background: 'linear-gradient(140deg,#0d9488,#14b8a6)', color: '#fff',
              fontSize: '1.125rem', boxShadow: '0 6px 14px -6px rgba(13,148,136,0.7)',
            }}
          >
            🦷
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '1.0625rem', fontWeight: 800, letterSpacing: '-0.01em' }}>Procedures</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
              {patientLabel}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close full editor"
            className="rounded-lg border bg-white text-slate-700 hover:bg-slate-100
                       dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
            style={{ fontSize: '0.8125rem', fontWeight: 700, padding: '0.5rem 0.85rem', borderColor: palette.border }}
          >
            ⤡ Close
          </button>
        </header>

        {/* Body — two columns */}
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 360px) 1fr',
            gap: 0,
            minHeight: 0,
          }}
        >
          {/* Left column — search / combos */}
          <div
            style={{
              borderRight: `1px solid ${palette.border}`,
              background: palette.bgAlt,
              padding: '1rem',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => setView('search')}
                aria-pressed={view === 'search'}
                className={`flex-1 px-2 py-2 rounded-lg border text-xs font-bold
                  ${view === 'search'
                    ? 'text-white border-transparent'
                    : 'bg-white text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600'}`}
                style={view === 'search'
                  ? { background: 'linear-gradient(135deg,#0d9488,#14b8a6)', boxShadow: '0 6px 14px -8px rgba(13,148,136,0.85)' }
                  : { borderColor: palette.border }}
              >
                🔎 Search
              </button>
              <button
                type="button"
                onClick={() => setView('combos')}
                aria-pressed={view === 'combos'}
                className={`flex-1 px-2 py-2 rounded-lg border text-xs font-bold
                  ${view === 'combos'
                    ? 'text-white border-transparent'
                    : 'bg-white text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600'}`}
                style={view === 'combos'
                  ? { background: 'linear-gradient(135deg,#0d9488,#14b8a6)', boxShadow: '0 6px 14px -8px rgba(13,148,136,0.85)' }
                  : { borderColor: palette.border }}
              >
                📘 Plans
              </button>
            </div>

            {view === 'search' && (
              <QuickSearchAdd
                visit={visit}
                viewer={viewer}
                onCreated={reload}
                palette={palette}
                autoFocus
              />
            )}
            {view === 'combos' && (
              <SavedCombosPicker
                visit={visit}
                viewer={viewer}
                onAttached={reload}
                palette={palette}
                onClose={() => setView('search')}
              />
            )}
          </div>

          {/* Right column — chip list */}
          <div
            style={{
              padding: '1rem',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              minWidth: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.9375rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
                Today's procedures
              </span>
              <span style={{ fontSize: '0.75rem', opacity: 0.7, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {sittings.length} item{sittings.length === 1 ? '' : 's'}
              </span>
            </div>

            {sittings.length === 0 ? (
              <div
                style={{
                  padding: '2rem 1rem',
                  textAlign: 'center',
                  border: `1px dashed ${palette.border}`,
                  borderRadius: 12,
                  color: palette.muted,
                  fontSize: '0.8125rem',
                }}
              >
                Use the search panel on the left to add a procedure to this visit.
              </div>
            ) : (
              <ProcedureChipList
                sittings={sittings}
                viewer={viewer}
                onAction={handleAction}
                onDelete={handleDelete}
                compact={false}
                currentVisitDoctorMemberId={visit?.doctor_member_id || null}
              />
            )}
          </div>
        </div>

        {/* Footer — totals + save-as-combo */}
        <footer
          style={{
            padding: '0.75rem 1.25rem',
            borderTop: `1px solid ${palette.border}`,
            background: palette.bgAlt,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <TotalsRibbon sittings={sittings} palette={palette} />
          </div>
          <SaveAsCombo
            sittings={sittings}
            palette={palette}
            onSaved={reload}
          />
        </footer>
      </div>

      {dialogs}
    </div>
  );
}
